import {
    AttachedFiles,
    type AttachedFilesRequest,
    type IHasAttachedFiles,
} from "legacyComponents/FileUploadContainer.types";
import {
    type IHasLineItems,
    type ILineItemContainerData,
    type ILineItemData,
    type ILineItemResponse,
    LineItem,
    LineItemType,
    LinkedCostItemTypes,
    PriceTypes,
} from "legacyComponents/LineItemContainer.types";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";

import type { CostTypes } from "@btnet/services-btnet/handlers/models/costTypes";
import { DocumentInstanceType } from "@btnet/services-btnet/handlers/models/documentInstanceType";
import type { JobTaxVersion } from "@btnet/services-btnet/handlers/models/jobTaxVersion";
import { MarkedAs } from "@btnet/services-btnet/handlers/models/markedAs";
import type { MarkupColumnTypes } from "@btnet/services-btnet/handlers/models/markupColumnTypes";
import type { ProposalCostCategoryResponse } from "@btnet/services-btnet/handlers/models/proposalCostCategoryResponse";
import type { ReadonlyProposalResponse } from "@btnet/services-btnet/handlers/models/readonlyProposalResponse";
import type { TaxMethod } from "@btnet/services-btnet/handlers/models/taxMethod";

import {
    BTSelectItem,
    type IBaseEntity,
    JobInfo,
    mapBTServiceDropdownToSelectItem,
    RelatedRFIs,
} from "types/apiResponse/apiResponse";
import { type CustomFieldAssociatedType } from "types/enum";

import {
    calculateBuilderCost,
    calculateMarginPercentageFromMarkup,
} from "utilities/lineItem/lineItemHelper";
import { isNullOrWhitespace } from "utilities/string/string";

import { type ICustomFieldFormValues } from "commonComponents/entity/customField/CustomFieldContainer/CustomFieldContainer";
import {
    type ICustomFieldOptionsList,
    type ICustomFieldResponse,
    type IHasCustomFieldContainer,
} from "commonComponents/entity/customField/CustomFieldContainer/CustomFieldContainer.types";
import {
    DiscussionsEntity,
    type IHasDiscussionContainer,
} from "commonComponents/entity/discussion/DiscussionContainer/DiscussionContainer.api.types";
import { RecentItemsTooltipConfig } from "commonComponents/entity/recentItemsTooltip/RecentItemsTooltip";
import {
    RelatedInvoice,
    RelatedPurchaseOrder,
} from "commonComponents/entity/relatedItem/RelatedItem.types";
import { type ILineItemRow } from "commonComponents/utilities/LineItemContainer/types/LineItem.interfaces";

import { type IChangeOrderLineItem } from "entity/changeOrder/ChangeOrderLineItemContainer/ChangeOrderLineItemContainer.types";
import {
    isChangeOrderDeclined,
    isChangeOrderNew,
    isChangeOrderOwnerDecided,
    isChangeOrderUndecided,
} from "entity/changeOrder/common/changeOrder.utilities";
import { ChangeOrderStatuses, type ResetOptionTypes } from "entity/changeOrder/common/types";
import { type DisplayFormatOptions } from "entity/changeOrder/DisplayColumnsSelect/DisplayColumnsSelect.api.types";
import { type ITaxGroup } from "entity/tax/common/tax.types";
import { TaxRateBreakdownInfo } from "entity/tax/common/TaxRateBreakdown/TaxRateBreakdown.api.types";
import { type VendorTypes } from "entity/vendor/common/vendor.types";

import { type default as ChangeOrderJson } from "./ChangeOrder.api.json";

export type ChangeOrderFormActions =
    | undefined
    | "save"
    | "saveAndClose"
    | "saveAndRelease"
    | "delete"
    | "unrelease"
    | "resend"
    | "resetToPending"
    | "approve"
    | "approveAndPay"
    | "decline"
    | "newInvoice"
    | "newBidPackage"
    | "newScheduleItem"
    | "sendToTheHomeDepotCart"
    | "checkedActions";

export enum ChangeOrderPresentingScreen {
    None = 0,
    FromRFI = 1,
    FromBidRequest = 2,
    FromSelection = 3,
    FromSelectionChoiceLineItem = 4,
    FromLeadProposalEstimateLineItem = 5,
    LeadOpportunity = 6,
    ScheduleItem = 7,
}

class PreApprovalAttachments implements IHasAttachedFiles {
    constructor(data: typeof ChangeOrderJson) {
        this.attachedFiles = new AttachedFiles(data.attachedFilesPreApproval);
        this.builderId = data.builderId;
        this.jobId = data.jobId;
        this.id = data.id;
    }
    documentInstanceType: DocumentInstanceType = DocumentInstanceType.ChangeOrder;
    name: string = "Pre-Approval";
    attachedFiles: AttachedFiles;
    id: number;
    jobId: number | undefined;
    builderId: number;
}

class PostApprovalAttachments implements IHasAttachedFiles {
    constructor(data: typeof ChangeOrderJson) {
        this.attachedFiles = new AttachedFiles(data.attachedFilesPostApproval);
        this.builderId = data.builderId;
        this.jobId = data.jobId;
        this.id = data.id;
    }
    documentInstanceType: DocumentInstanceType = DocumentInstanceType.ChangeOrder;
    name: string = "Post-Approval";
    attachedFiles: AttachedFiles;
    id: number;
    jobId: number | undefined;
    builderId: number;
}

export interface IChangeOrderFormValues {
    changeOrderId: string;
    title: string;
    introductionText: string;
    closingText: string;
    notes?: string;
    subsOnlyNotes?: string;
    ownerOnlyNotes?: string;
    deadLine?: Date;
    subIDs: number[];
    customFields: ICustomFieldFormValues[];
    attachedFiles: AttachedFilesRequest;
    invoiceOnOwnerApproval?: boolean;
    worksheetValid?: boolean;
    dataUrl: string;
    isCanvasEmpty: boolean;
    signatureToSave?: SignaturePad.Point[][];
    saveSignature: boolean;
    requiredApprovers: number[];
    requireSignatures: boolean;
    displayFormatOptions: DisplayFormatOptions;

    // update values
    resetFiles?: ResetOptionTypes;
    notifyNewSubs?: boolean;
    notifySubs?: boolean;
    notifySubsResetText?: string;
    approvalComments?: string;
    approvalDate?: moment.Moment;
    resetAmendedText?: ResetOptionTypes;
    approvalDisclaimer: boolean;

    amountInvoiced?: number;
    taxMethod: TaxMethod;
    taxGroupId?: number;

    // only used for powering the readonly client view for legacy change orders
    changeOrderLineItems: ChangeOrderLineItem[];
}

class CustomChangeOrderId {
    constructor(data: typeof ChangeOrderJson.changeOrderId) {
        this.value = data.value;
        this.title = data.title;
        this.prefix = data.prefix;
    }
    value: string;
    title: string;
    prefix: string;
}

class ApprovalDetails {
    constructor(data: typeof ChangeOrderJson.approvalDetails) {
        this.signature = data.signature ?? undefined;
        this.comments = data.comments && data.comments.value;
        this.status = data.status;
        this.statusText = data.statusText;
    }
    signature?: string;
    comments: string;
    status: number;
    statusText: string;
}

class RelatedBid {
    constructor(data: (typeof ChangeOrderJson.relatedBids)[0]) {
        this.bidId = data.bidId;
        this.bidPackageTitle = data.bidPackageTitle;
        this.subName = data.subName;
        this.isAccepted = data.isAccepted;
        this.isDeclined = data.isDeclined;
    }
    bidId: number;
    bidPackageTitle: string;
    subName: string;
    isAccepted: boolean;
    isDeclined: boolean;
}

class RelatedSchedule {
    constructor(data: (typeof ChangeOrderJson.relatedScheduleItems)[0]) {
        this.id = data.id;
        this.title = data.title;
    }
    id: number;
    title: string;
}

export class ChangeOrderEntity
    implements IBaseEntity, IHasDiscussionContainer, IHasLineItems, IHasCustomFieldContainer
{
    constructor(data: typeof ChangeOrderJson) {
        this.id = data.id;
        this.jobId = data.jobId;
        this.builderId = data.builderId;
        this.relatedProposalId = data.relatedProposalId;
        this.scopeOfWorkId = data.scopeOfWorkId;
        const { externalId, externalBuilderId, externalJobId } = data as {
            externalId?: string;
            externalBuilderId?: string;
            externalJobId?: string;
        };
        this.externalId = externalId;
        this.externalBuilderId = externalBuilderId;
        this.externalJobId = externalJobId;
        this.jobInfo = data.jobInfo && new JobInfo(data.jobInfo);
        this.jobName = data.jobName;
        if (data.jobOpenedDate && !isNullOrWhitespace(data.jobOpenedDate)) {
            this.jobOpenedDate = new Date(data.jobOpenedDate);
        }
        this.instanceKey = uuidv4();
        this.title = data.title.value;
        this.signaturePrefs = data.signaturePrefs
            ? data.signaturePrefs.value.map((item) => new BTSelectItem(item))
            : undefined;
        this.approvedBy = data.approvedBy;
        this.subsOnlyNotes = data.subsOnlyNotes && data.subsOnlyNotes.value;
        this.notes = data.notes && data.notes.value;
        this.ownerOnlyNotes = data.ownerOnlyNotes && data.ownerOnlyNotes.value;
        this.deadLine =
            data.deadLine && data.deadLine.value ? new Date(data.deadLine.value) : undefined;
        this.createdBy = data.createdBy;
        this.createdById = data.createdById;
        this.createdByDate = data.createdByDate && new Date(data.createdByDate.value);
        this.descHasHTML = data.descHasHTML;
        if (data.viewedByOwnerOn && !isNullOrWhitespace(data.viewedByOwnerOn)) {
            this.viewedByOwnerOn = new Date(data.viewedByOwnerOn);
        }
        this.customChangeOrderId =
            data.changeOrderId && new CustomChangeOrderId(data.changeOrderId);
        this.introductionText = data.introductionText?.value;
        this.closingText = data.closingText?.value;
        this.description = data.description.value;
        this.descriptionPlainText = data.descriptionPlainText ?? undefined;
        this.amendments = data.amendments && data.amendments.value;
        this.approvalDetails = new ApprovalDetails(data.approvalDetails);
        this.approvalStatus = this.approvalDetails.status;
        this.ownerPrice = data.ownerPrice && data.ownerPrice.value;
        this.builderCost = data.builderCost && data.builderCost.value;
        this.useLineItems = data.useLineItems && data.useLineItems.value;
        this.priceType = this.useLineItems ? PriceTypes.LineItems : PriceTypes.FlatFee;
        this.lineItems = data.lineItems as unknown as ILineItemData;
        this.costCategories = (data as { costCategories: BTSelectItem[] }).costCategories;
        this.displayFormatOptions = data.displayFormatOptions.value;
        this.hasLineItemsCanLinkToBid = data.hasLineItemsCanLinkToBid;
        this.hasLineItemsCanLinkToPo = data.hasLineItemsCanLinkToPo;
        this.subIDs = data.subIDs ? data.subIDs.value.map((item) => new BTSelectItem(item)) : [];
        this.canAdd = data.canAdd;
        this.canEdit = data.canEdit;
        this.canDelete = data.canDelete;
        this.canAddBids = data.canAddBids;
        this.canEditBids = data.canEditBids;
        this.canViewBids = data.canViewBids;
        this.canAddPurchaseOrders = data.canAddPurchaseOrders;
        this.canEditPurchaseOrders = data.canEditPurchaseOrders;
        this.canViewPurchaseOrders = data.canViewPurchaseOrders;
        this.canAddScheduleItems = data.canAddScheduleItems;
        this.canViewWithoutPermission = data.canViewWithoutPermission;
        this.canEditWithoutPermission = data.canEditWithoutPermission;
        this.canViewPrice = data.canViewPrice;
        this.isSelectionsReimaginedJob = data.isSelectionsReimaginedJob ?? false;
        this.canSendToHomeDepotCart = data.canSendToHomeDepotCart ?? false;
        this.attachedFilesPreApproval = new PreApprovalAttachments(data);
        this.attachedFilesPostApproval =
            data.attachedFilesPostApproval && new PostApprovalAttachments(data);
        this.relatedRFIs = data.relatedRFIs && new RelatedRFIs(data.relatedRFIs);
        this.relatedBids = data.relatedBids
            ? data.relatedBids.map((item) => new RelatedBid(item))
            : undefined;
        this.relatedScheduleItems = data.relatedScheduleItems
            ? data.relatedScheduleItems.map((item) => new RelatedSchedule(item))
            : undefined;
        this.relatedPurchaseOrders = data.relatedPurchaseOrders
            ? data.relatedPurchaseOrders.map((item) => new RelatedPurchaseOrder(item))
            : [];
        this.relatedOwnerInvoices = data.relatedOwnerInvoices
            ? data.relatedOwnerInvoices.map((item) => new RelatedInvoice(item))
            : [];
        this.recentItemsTooltip =
            data.recentItemsTooltip === undefined
                ? undefined
                : new RecentItemsTooltipConfig(data.recentItemsTooltip);
        this.canMapToNewBidPackage = data.canMapToNewBidPackage;
        this.approvalStatusMessage = data.approvalStatusMessage || undefined;
        if (!isNullOrWhitespace(data.approvalDate)) {
            this.approvalDate = moment(data.approvalDate);
        }
        this.showNoCustomFieldsMessage = data.showNoCustomFieldsMessage;
        this.customFieldOptions = data.customFieldOptions;
        this.customFields = data.customFields;
        this.customFieldAssociatedType = data.customFieldAssociatedType;
        this.customFieldsCanConfigure = data.customFieldsCanConfigure;
        this.costItemUpdateMessage = data.costItemUpdateMessage ?? null;
        this.discussions = new DiscussionsEntity(data.discussions);
        this.invoiceOnOwnerApproval =
            data.invoiceOnOwnerApproval && data.invoiceOnOwnerApproval.value;
        this.negativeAmountMessage = data.negativeAmountMessage;
        this.enableInvoiceOnOwnerApprovalCheckbox = data.enableInvoiceOnOwnerApprovalCheckbox;
        this.userCanInvoiceChangeOrder = data.userCanInvoiceChangeOrder;
        this.changeOrderIsInvoiceable = data.changeOrderIsInvoiceable;
        this.assignedToMessage = data.assignedToMessage && data.assignedToMessage.value;
        this.hasCostCodesFeature = data.hasCostCodesFeature;
        this.hasExistingInvoices = data.hasExistingInvoices;
        this.showApproveAndPayButton = data.showApproveAndPayButton;
        this.requiredApprovers =
            data.requiredApprovers?.map(
                (approver: { id: number; name: string; selected?: boolean }) =>
                    new BTSelectItem({ ...approver, selected: approver.selected ?? false }),
            ) || [];

        this.draftProposal = data.draftProposal as unknown as ReadonlyProposalResponse;

        const userCanViewCOPrices = data.canViewWithoutPermission || data.canViewPrice;

        this.lineItemContainerData = {
            canAddOwnerPayments: data.canAddOwnerPayments,
            canEditCostCodes: data.lineItems ? data.lineItems.canEditCostCodes : false,
            canReorderLineItems: true,
            costCodeSelectorReadOnly: isChangeOrderDeclined(this),
            displayTitle: true,
            entityId: data.id,
            entityPending: isChangeOrderUndecided(this),
            hasEditPermission:
                userCanViewCOPrices && (this.canEdit || (isChangeOrderNew(this) && this.canAdd)),
            hasPercentInvoicePermission: data.userCanInvoiceChangeOrder,
            hasPriceViewingPermission: userCanViewCOPrices,
            inactiveCostCodeCutoffDate: data.inactiveCostCodeCutoffDate,
            isOwnerApproved: this.approvalStatus === ChangeOrderStatuses.APPROVED,
            jobId: data.jobId,
            linkedCostItemType: LinkedCostItemTypes.ChangeOrder,
            lineItemReadOnly:
                (!isChangeOrderNew(this) && !this.canEdit) || isChangeOrderOwnerDecided(this),
            onlyLockMarkup: isChangeOrderOwnerDecided(this),
            pageType: "change-order",
            pageTypeEnum: LineItemType.ChangeOrder,
            priceType: this.priceType,
            showCatalog: true,
            showMarkupTotal: true,
            showToOwner: data.showLineItemsToOwner
                ? data.showLineItemsToOwner.value
                : !!data.lineItems, // not present on the owner response.
            showVarianceCodes: false, // this may not be needed. I think the line item container defaults this false if this key isn't present.
            unitCostToolTipText:
                "The <b> Unit Cost</b> field on a Change Order represents the dollar amount of each item associated with the cost code.",
        };

        if (data.lineItems) {
            this.lineItemInitialValues = data.lineItems.value
                ? data.lineItems.value.map(
                      (x) =>
                          new LineItem(
                              x as unknown as ILineItemResponse,
                              this.lineItemContainerData.pageType,
                              this.lineItemContainerData.pageTypeEnum,
                          ),
                  )
                : [];
        }

        this.changeOrderLineItems = data.lineItems?.value.map(
            // eslint-disable-next-line no-constant-binary-expression
            (x) => new ChangeOrderLineItem(x as unknown as ILineItemResponse) ?? [],
        );

        // Group/sub-group structure tied to the change order's scope of work (line items not yet nested here).
        this.formatData =
            (data as { formatData?: ProposalCostCategoryResponse[] }).formatData ?? [];

        // Tax Fields
        this.taxMethod = data.taxMethod && mapBTServiceDropdownToSelectItem(data.taxMethod);
        this.taxGroupId = data.defaultTaxGroup?.taxGroupId ?? data.taxGroupId;
        this.taxRateBreakdown =
            data.taxRateBreakdown && new TaxRateBreakdownInfo(data.taxRateBreakdown);
        this.totalWithTax = data.totalWithTax;
        this.effectiveTaxVersion = data.effectiveTaxVersion as JobTaxVersion;
        this.defaultTaxGroup = data.defaultTaxGroup;

        // Owner only fields
        this.disclaimer = data.disclaimer ? data.disclaimer.value : undefined;
        this.expirationMessage = data.expirationMessage || undefined;
        this.canApprove = data.canApprove || undefined;
        this.requireSignatureForApproval = data.requireSignatureForApproval || undefined;
        this.approvalDisclaimer = false;
    }

    builderId: number;
    relatedProposalId: number;
    scopeOfWorkId: number;
    formatData: ProposalCostCategoryResponse[];
    externalId?: string;
    externalBuilderId?: string;
    externalJobId?: string;
    jobInfo: JobInfo;
    jobName: string;
    jobOpenedDate: Date;
    instanceKey: string;
    title: string;
    signaturePrefs?: BTSelectItem[];
    createdByDate?: Date;
    approvedBy?: string;
    subsOnlyNotes?: string;
    notes?: string;
    ownerOnlyNotes?: string;
    deadLine?: Date;
    createdBy?: string;
    createdById: number;
    descHasHTML?: boolean;
    viewedByOwnerOn?: Date;
    customChangeOrderId: CustomChangeOrderId;
    introductionText: string;
    closingText: string;
    description: string;
    descriptionPlainText?: string;
    amendments?: string;
    approvalDetails: ApprovalDetails;
    ownerPrice?: number;
    builderCost?: number;
    useLineItems?: boolean;
    priceType: PriceTypes;
    lineItemInitialValues: LineItem[];
    lineItems: ILineItemData;
    costCategories: BTSelectItem[];
    displayFormatOptions: DisplayFormatOptions;
    lineItemContainerData: ILineItemContainerData;
    hasLineItemsCanLinkToBid?: boolean;
    hasLineItemsCanLinkToPo?: boolean;
    subIDs: BTSelectItem[];
    canAdd: boolean;
    canEdit: boolean;
    canDelete: boolean;
    canAddBids?: boolean;
    canEditBids?: boolean;
    canViewBids?: boolean;
    canAddPurchaseOrders?: boolean;
    canEditPurchaseOrders?: boolean;
    canViewPurchaseOrders?: boolean;
    canAddScheduleItems?: boolean;
    canViewWithoutPermission: boolean;
    canEditWithoutPermission: boolean;
    canViewPrice: boolean;
    isSelectionsReimaginedJob: boolean;
    canSendToHomeDepotCart: boolean;
    attachedFiles: AttachedFiles;
    attachedFilesPreApproval: PreApprovalAttachments;
    attachedFilesPostApproval?: PostApprovalAttachments;
    relatedRFIs?: RelatedRFIs;
    relatedBids?: RelatedBid[];
    relatedScheduleItems?: RelatedSchedule[];
    relatedPurchaseOrders: RelatedPurchaseOrder[];
    relatedOwnerInvoices: RelatedInvoice[];
    recentItemsTooltip?: RecentItemsTooltipConfig;
    canMapToNewBidPackage: boolean;
    approvalStatusMessage?: string;
    approvalDate?: moment.Moment;
    id: number;
    jobId: number;
    approvalStatus: ChangeOrderStatuses;
    showNoCustomFieldsMessage: boolean;
    customFieldOptions: ICustomFieldOptionsList;
    customFields: ICustomFieldResponse[];
    customFieldAssociatedType: CustomFieldAssociatedType;
    customFieldsCanConfigure: boolean;
    costItemUpdateMessage: string | null;
    discussions: DiscussionsEntity;
    invoiceOnOwnerApproval?: boolean;
    negativeAmountMessage?: string;
    approvalDisclaimer: boolean;
    enableInvoiceOnOwnerApprovalCheckbox?: boolean;
    userCanInvoiceChangeOrder?: boolean;
    changeOrderIsInvoiceable?: boolean;
    assignedToMessage?: string;
    changeOrderLineItems: ChangeOrderLineItem[];
    hasCostCodesFeature: boolean;
    hasExistingInvoices: boolean;
    showApproveAndPayButton: boolean;
    requiredApprovers: BTSelectItem[];
    draftProposal?: ReadonlyProposalResponse;

    // Tax Fields
    taxMethod: BTSelectItem[] | undefined;
    taxGroupId?: number;
    taxRateBreakdown?: TaxRateBreakdownInfo;
    totalWithTax?: number;
    effectiveTaxVersion: JobTaxVersion;
    defaultTaxGroup: ITaxGroup | null;

    // Owner only fields
    disclaimer?: string;
    expirationMessage?: string;
    canApprove?: boolean;
    requireSignatureForApproval?: boolean;
}

/**
 * Request object for the ResetStatus API call. Not actually for resetting the status. This can be used for any status, although we will only use this
 * for putting the status to unreleased, or to approved/declined for owner portal.
 */
export interface IChangeOrderResetStatusRequest {
    approvalStatus: ChangeOrderStatuses;
    approvalComments?: string;
    updateCostItems?: boolean;
    signatureUrl: string;
    signatureToSave: SignaturePad.Point[][];
}

export interface IUpdateBidStatusFromChangeOrderRequest {
    changeOrderId: number;
    details?: string;
    notifyLoser: boolean;
    notifyWinner?: boolean;
}

export interface IChangeOrderSaveRequest
    extends Omit<
        IChangeOrderFormValues,
        | "taxGroupId"
        | "description"
        | "lineItems"
        | "changeOrderLineItems"
        | "ownerPrice"
        | "builderCost"
    > {
    approvalStatus: ChangeOrderStatuses;
    useLineItems: boolean;
    updateCostItems?: boolean;
    taxMethod: TaxMethod;
    taxGroupId: number | null;
    signatureUrl: string;
    lineItems?: LineItem[];
    changeOrderLineItems?: ChangeOrderLineItem[];
    formatGroups?: IUpdateScopeOfWorkGroupExpandedStatusRequest[];
}

// Mirrors Global.Core.Types.ScopesOfWork.UpdateScopeOfWorkGroupExpandedStatusRequest - the backend
// only persists expand/collapse for change order format groups, not the full group shape.
export interface IUpdateScopeOfWorkGroupExpandedStatusRequest {
    scopeOfWorkId: number;
    jobId: number;
    builderId: number;
    groupId: number;
    isExpanded: boolean;
}

export class ChangeOrderResetStatusResponse {
    constructor(data: any) {
        this.canPayOnline = data.canPayOnline;
        this.invoiceId = data.invoiceId;
        this.successMessage = data.successMessage;
    }

    canPayOnline: boolean;
    invoiceId?: number;
    successMessage?: string;
}

export class ChangeOrderVarianceImportResponse {
    constructor(data: any) {
        this.title = data.title;
        this.scopeOfWork = data.scopeOfWork;
    }

    title: string;
    scopeOfWork: string;
}

interface IChangeOrderLineItemInput {
    lineItemId?: number | null;
    id: number;
    unitCost?: number | null;
    quantity?: number | null;
    unitType?: string | null;
    unit?: string | null;
    ownerPrice?: number | null;
    markupAmount: number;
    markupPrice?: number | null;
    markupPerUnit?: number | null;
    markupPercent: number;
    markupPercentage?: number | null;
    margin?: number | null;
    costCode?: number;
    costCodeId?: number | null;
    itemTitle?: string | null;
    title?: string | null;
    costCodeItemId?: number | null;
    costItemId?: number | null;
    description?: string | null;
    internalNotes?: string | null;
    costTypes?: CostTypes[] | null;
    markedAs?: MarkedAs;
    isExpanded?: boolean;
    _isInEdit?: boolean;
    amountInvoiced?: number | null;
    retainageWithheld?: number | null;
    autoFocusedField?: string;
    markupType?: MarkupColumnTypes | null;
    markupColumn?: MarkupColumnTypes | 0 | null;
    vendorProductId?: number | null;
    vendorType?: VendorTypes | null;
    isDiscontinued?: boolean | null;
    costCodeTitle?: string | null;
    taxGroupId?: number | null;
    totalWithTax?: number | null;
    selectionOptionGroupID?: number | null;
    selectionOptionGroupId?: number | null;
}

export class ChangeOrderLineItem implements ILineItemRow, IChangeOrderLineItem {
    constructor(data: IChangeOrderLineItemInput) {
        this.id = data.lineItemId ?? data.id;
        this.builderCost = calculateBuilderCost(data.unitCost ?? 0, data.quantity ?? null);
        this.quantity = data.quantity ?? 0;
        this.unitCost = data.unitCost ?? 0;
        this.unit = data.unitType ?? data.unit ?? "";
        this.ownerPrice = data.ownerPrice ?? 0;
        this.markupAmount = data.markupAmount ?? data.markupPrice;
        this.markupPerUnit = data.markupPerUnit ?? 0;
        this.markupPercent = data.markupPercent ?? data.markupPercentage;
        this.margin =
            data.margin ??
            calculateMarginPercentageFromMarkup(
                data.markupPercent ?? data.markupPercentage ?? 0,
                data.markupAmount ?? data.markupPrice ?? 0,
                data.markupPerUnit ?? 0,
                data.unitCost ?? 0,
                data.quantity ?? 0,
            );
        this.costCodeId = data.costCode ?? data.costCodeId ?? null;
        this.itemTitle = data.itemTitle ?? data.title ?? null;
        this.costItemId = data.costCodeItemId ?? data.costItemId ?? null;
        this.lineItemType = LineItemType.ChangeOrder;
        this.isExpanded = data.isExpanded ?? false;
        this.description = data.description ?? "";
        this.internalNotes = data.internalNotes ?? null;
        this.costTypes = data.costTypes ?? [];
        this.markedAs = data.markedAs ?? MarkedAs.None;
        this.isEditable = true;
        this._isInEdit = data._isInEdit;
        this.amountInvoiced = data.amountInvoiced ?? 0;
        this.retainageWithheld = data.retainageWithheld ?? undefined;
        this.autoFocusedField = data.autoFocusedField;
        const markupType = data.markupType ?? data.markupColumn;
        this.markupType = markupType as MarkupColumnTypes;
        this.vendorProductId = data.vendorProductId ?? undefined;
        this.vendorType = data.vendorType ?? undefined;
        this.isDiscontinued = data.isDiscontinued ?? undefined;
        this.costCodeTitle = data.costCodeTitle ?? null;
        this.taxGroupId = data.taxGroupId ?? undefined;
        this.totalWithTax = data.totalWithTax ?? undefined;
        this.selectionOptionGroupId =
            data.selectionOptionGroupID ?? data.selectionOptionGroupId ?? null;
    }

    id: number;
    unitCost: number;
    unit: string;
    quantity: number;
    builderCost: number;
    markupType: MarkupColumnTypes;
    markupPercent: number;
    markupPerUnit: number;
    markupAmount: number;
    ownerPrice: number;
    margin: number;
    description: string;
    internalNotes: string | null;
    costItemId: number | null;
    costTypes: CostTypes[] | null;
    markedAs: MarkedAs;
    parentId?: number | undefined;
    costCodeId: number | null;
    itemTitle: string | null;
    lineItemType: LineItemType;
    amountInvoiced: number | null;
    retainageWithheld?: number;
    costCodeTitle: string | null;

    // IGridLineItemViewRow
    _isInEdit?: boolean;
    isSelected?: boolean;
    isExpanded?: boolean;
    isEditable: boolean;
    autoFocusedField?: string;

    // IVendorProductLineItem
    vendorProductId?: number;
    vendorType?: VendorTypes;
    isDiscontinued?: boolean;

    // ITaxLineItem
    taxGroupId?: number;
    totalWithTax?: number;

    selectionOptionGroupId: number | null;
}

export interface IChangeOrderResetToPendingFormValues {
    resetAmendedTextSelection: ResetOptionTypes;
    resetFilesSelection: ResetOptionTypes;
    notifySubs: boolean;
    notifySubsResetText: string;
}

export const changeOrderEntityName = "Change Order";
